import { ArrowRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Card } from '@/components/ui/Card'
import {
  filterSectionGroups,
  filterSectionItems,
  flattenLeaves,
  sectionGroups,
  type SectionItem,
} from '@/config/navigation'
import { usePermissions } from '@/hooks/use-permissions'
import { PageLayout } from '@/layouts/PageLayout'
import { type User, type UserSection } from '@/models/domain/user'

interface TabDef {
  key: string
  label: string
}

const USER_TAB: TabDef = { key: 'user', label: 'User' }

function getVisibleTabs(user: User, hasAccess: (sections: UserSection[]) => boolean): TabDef[] {
  const groupTabs = sectionGroups
    .filter((group) => filterSectionItems(group.items, user, hasAccess).length > 0)
    .map((group) => ({ key: group.key, label: group.title }))
  return [USER_TAB, ...groupTabs]
}

function getAccessLevelLabel(user: { sys_op?: boolean; sys_admin?: boolean } | null): string {
  if (user?.sys_op) return 'Root Operator'
  if (user?.sys_admin) return 'System Admin'
  return 'Standard User'
}

function TabBar({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: TabDef[]
  activeTab: string
  onTabChange: (tab: string) => void
}) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <div className="flex -mb-px" role="tablist" aria-label="Dashboard tabs">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            id={`tab-${key}`}
            onClick={() => {
              onTabChange(key)
            }}
            onKeyDown={(e) => {
              const tabKeys = tabs.map((t) => t.key)
              const currentIndex = tabKeys.indexOf(key)
              if (e.key === 'ArrowRight') {
                e.preventDefault()
                const next = tabKeys[(currentIndex + 1) % tabKeys.length]
                onTabChange(next)
                document.getElementById(`tab-${next}`)?.focus()
              }
              if (e.key === 'ArrowLeft') {
                e.preventDefault()
                const prev = tabKeys[(currentIndex - 1 + tabKeys.length) % tabKeys.length]
                onTabChange(prev)
                document.getElementById(`tab-${prev}`)?.focus()
              }
            }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              key === activeTab
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            aria-selected={key === activeTab}
            aria-controls={`tabpanel-${key}`}
            role="tab"
            tabIndex={key === activeTab ? 0 : -1}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}

function UserTab({
  user,
}: {
  user: {
    first_name: string
    last_name: string
    email: string
    sys_op?: boolean
    sys_admin?: boolean
  }
}) {
  return (
    <>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">User Information</h3>
      <dl className="grid grid-cols-1 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 dark:text-gray-400">Name</dt>
          <dd className="text-gray-900 dark:text-white font-medium">
            {user.first_name} {user.last_name}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-gray-400">Email</dt>
          <dd className="text-gray-900 dark:text-white font-medium">{user.email}</dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-gray-400">System Access</dt>
          <dd className="text-gray-900 dark:text-white font-medium">{getAccessLevelLabel(user)}</dd>
        </div>
      </dl>
    </>
  )
}

function SectionCard({ section }: { section: SectionItem & { path: string } }) {
  const Icon = section.icon

  return (
    <Link
      to={section.path}
      className="flex items-center gap-4 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 text-left transition-colors hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 group"
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{section.name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{section.description}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors flex-shrink-0" />
    </Link>
  )
}

function SectionGrid({ sections }: { sections: SectionItem[] }) {
  const leaves = flattenLeaves(sections)
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {leaves.map((section) => (
        <SectionCard key={section.path} section={section} />
      ))}
    </div>
  )
}

export function HomePage() {
  const { user, hasAccess } = usePermissions()
  const [activeTab, setActiveTab] = useState<string>('user')

  const visibleTabs = useMemo(
    () => (user ? getVisibleTabs(user, hasAccess) : [USER_TAB]),
    [user, hasAccess]
  )

  const filteredGroups = useMemo(
    () => (user ? filterSectionGroups(user, hasAccess) : []),
    [user, hasAccess]
  )

  const safeActiveTab = visibleTabs.some((t) => t.key === activeTab) ? activeTab : 'user'

  return (
    <PageLayout
      title="Dashboard"
      description={user ? `Welcome back, ${user.first_name} ${user.last_name}!` : undefined}
    >
      <Card padding="none">
        <TabBar tabs={visibleTabs} activeTab={safeActiveTab} onTabChange={setActiveTab} />
        <div
          className="p-6"
          id={`tabpanel-${safeActiveTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${safeActiveTab}`}
        >
          {safeActiveTab === 'user' && user && <UserTab user={user} />}
          {filteredGroups
            .filter((group) => group.key === safeActiveTab)
            .map((group) => (
              <SectionGrid key={group.key} sections={group.items} />
            ))}
        </div>
      </Card>
    </PageLayout>
  )
}
